import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import omit from "lodash/omit";
import {FormProvider, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {FiX} from "react-icons/fi";
import {LuCheck} from "react-icons/lu";
import * as Yup from "yup";

import {randomColorPreset} from "@/components/colorPicker/ColorPicker.tsx";
import {ColorPickerField} from "@/components/form/fields/ColorPickerField/ColorPickerField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {GQL_SAVE_TAG, GQL_SERVICE_TAGS, SaveTagResponse} from "@/lib/queries/checks.ts";
import {ServiceTag} from "@/lib/types/checks.ts";
import {ResponseStatus} from "@/lib/types/common.ts";

interface TagFormFields {
  id?: number;
  tag: string;
  color_hex: string;
}

type TagFormKeys = keyof TagFormFields;

interface TagFormProps {
  formId?: string;
  tag?: ServiceTag;
  onSave?: (tag: ServiceTag) => void;
  showActionButtons?: boolean;
  inputFieldClassname?: string;
  autoFocus?: boolean;
}

export const TagForm = ({
  formId,
  tag,
  onSave,
  showActionButtons = true,
  inputFieldClassname,
  autoFocus,
}: TagFormProps) => {
  const form = useForm<TagFormFields>({
    resolver: yupResolver<TagFormFields>(
      Yup.object({
        id: Yup.number().optional(),
        tag: Yup.string().max(100).required("This field cannot be blank."),
        color_hex: Yup.string().required("This field cannot be blank."),
      })
    ),
    defaultValues: tag || {color_hex: randomColorPreset()},
  });

  const [saveTags] = useMutation<SaveTagResponse>(GQL_SAVE_TAG, {refetchQueries: [GQL_SERVICE_TAGS]});

  const onSubmit = async (tagData: TagFormFields) => {
    const resp = await saveTags({
      variables: {tag: omit(tagData, ["usage_count"])},
    });
    const response = resp.data?.response;
    if (response?.status === ResponseStatus.SUCCESS) {
      toast.success("Tag saved successfully");
      onSave?.(response.tag);
    } else if (response?.status === ResponseStatus.ERROR && response.message) {
      Object.entries(response.message).forEach(([fieldName, messageList]) => {
        form.setError(fieldName as TagFormKeys, {message: (messageList as string[])[0]});
      });
    }
  };

  return (
    <FormProvider {...form}>
      <form id={formId} onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <div className="d-flex align-items-center justify-items-center gap-2">
          <ColorPickerField name={"color_hex"} hasTextInput={false} />

          <InputField name={"tag"} className={inputFieldClassname} maxLength={100} autoFocus={autoFocus} />

          {form.formState.isDirty && showActionButtons && (
            <>
              <IconButton
                icon={LuCheck}
                className={"pulse-color-green-500"}
                variant={"ghost"}
                size={"fitIcon"}
                onClick={form.handleSubmit(onSubmit)}
                aria-label={"Save"}
              />
              <IconButton
                icon={FiX}
                className={"pulse-color-red-500"}
                variant={"ghost"}
                size={"fitIcon"}
                onClick={() => form.reset()}
                aria-label={"Cancel"}
              />
            </>
          )}
        </div>
      </form>
    </FormProvider>
  );
};
